import React from "react";
import { Box, Typography } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { MessageBubbleContainer, MessageContent } from "./SharedStyledComponents";

const PaymentRequestUpfrontMessage = ({
  message,
  isOwn,
  currentUser,
  storedUserId,
  isCurrentUserOwnerOrTeamMember,
  onOpenPaymentDialog,
  getReadReceiptStatus,
  formattedMessage,
}) => {
  // Get current user ID
  const currentUserId = currentUser?.userId ?? storedUserId;

  // Check if current user is in initiationFor array
  const isUserInInitiationFor =
    message.initiationFor && message.initiationFor.includes(currentUserId);

  // Hide payment_request_upfront messages from ALL business owners and team members
  // BUT only if they are NOT in the initiationFor array (which takes priority)
  if (isCurrentUserOwnerOrTeamMember && !isUserInInitiationFor) {
    return null;
  }

  // Get current user's order for payment details
  const orderId = message?.orderId;
  const userOrders = orderId?.groupOrder?.userOrders || [];
  const currentUserOrder = userOrders.find((userOrder) => {
    const userId = userOrder.userId?._id || userOrder.userId;
    return userId === currentUserId;
  });

  // Group-level remaining upfront (anyone can contribute if there's group remaining)
  const upfrontPercentage = orderId?.paymentSettings?.upfrontPercentage || 20;
  const groupSubtotal = orderId?.groupSubtotal || 0;
  const requiredGroupUpfront = (groupSubtotal * upfrontPercentage) / 100;
  const totalPaidAmount = orderId?.totalPaidAmount || 0;
  const groupRemainingUpfront = requiredGroupUpfront - totalPaidAmount;
  const hasGroupRemainingUpfront = groupRemainingUpfront > 0.01;

  // Per-user remaining upfront
  const currentSubtotal = currentUserOrder?.subtotal || 0;
  const requiredUpfrontAmount = (currentSubtotal * upfrontPercentage) / 100;
  const amountPaid = currentUserOrder?.amountPaid || 0;
  const remainingUpfrontPayment = requiredUpfrontAmount - amountPaid;
  const hasUserRemainingUpfrontPayment = remainingUpfrontPayment > 0.01;

  // Only enable when message is explicitly marked latest
  let isLatestPaymentRequest = message?.isLatestPaymentRequest === true;

  // Heuristic fallback
  if (message?.isLatestPaymentRequest === undefined) {
    const msgTs = message?.createdAt ? new Date(message.createdAt).getTime() : 0;
    const orderUpdatedTs = orderId?.updatedAt ? new Date(orderId.updatedAt).getTime() : 0;
    const withinWindow = orderUpdatedTs && msgTs && Math.abs(orderUpdatedTs - msgTs) <= 5000;
    if (withinWindow) {
      isLatestPaymentRequest = true;
    }
  }

  const tipEnableCore = hasGroupRemainingUpfront || hasUserRemainingUpfrontPayment;

  let matchesCurrentUpfront = false;
  if (typeof message?.amount === "number") {
    matchesCurrentUpfront = Math.abs(message.amount - groupRemainingUpfront) <= 0.011;
  } else if (typeof message?.description === "string") {
    const match = message.description.match(/Remaining upfront:\s*\$(\d+(?:\.\d+)?)/i);
    if (match) {
      const descAmount = parseFloat(match[1]);
      if (!isNaN(descAmount)) {
        matchesCurrentUpfront = Math.abs(descAmount - groupRemainingUpfront) <= 0.011;
      }
    }
  }

  const shouldEnableTipButton = 
    !message?.buttonDisabled && 
    (tipEnableCore || (isLatestPaymentRequest || matchesCurrentUpfront));

  const customMessageText = formattedMessage.text;

  return (
    <MessageBubbleContainer isOwn={isOwn}>
      {!isOwn && (
        <Typography
          variant="caption"
          sx={{
            color: "#374151",
            fontSize: { xs: "0.75rem", sm: "0.8rem" },
            fontWeight: 600,
            mb: 0.5,
            alignSelf: "flex-start",
          }}
        >
          {message.senderId?.fullName || message.senderId?.userName || "user"}
        </Typography>
      )}

      <MessageContent
        isOwn={isOwn}
        isTemp={message.isTemp}
        isOrder={false}
        isPayment={true}
        isRequest={false}
        idDraft={false}
        isDisabled={false}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            minWidth: "280px",
            maxWidth: "380px",
            cursor: "pointer",
            "&:hover": {
              opacity: 0.9,
            },
          }}
        >
          <Box
            sx={{
              color: isOwn ? "#FFFFFF" : "#000000",
              fontSize: "16px",
              fontWeight: 600,
              lineHeight: 1.3,
              textAlign: "left",
            }}
          >
            {customMessageText && customMessageText.includes("tip here") ? (
              <>
                {customMessageText.split("tip here")[0]}
                <Box
                  component="span"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (
                      shouldEnableTipButton &&
                      !message?.buttonDisabled &&
                      message?.orderId?._id
                    ) {
                      onOpenPaymentDialog?.(message);
                    }
                  }}
                  sx={{
                    color: shouldEnableTipButton ? (isOwn ? "#FDE047" : "#7C3AED") : "#D1D5DB",
                    cursor: shouldEnableTipButton ? "pointer" : "not-allowed",
                    textDecoration: shouldEnableTipButton ? "underline" : "none",
                    opacity: isLatestPaymentRequest ? 1 : 0.6,
                    "&:hover": {
                      color: shouldEnableTipButton ? (isOwn ? "#FEF08A" : "#6D28D9") : "#D1D5DB",
                    },
                  }}
                >
                  tip here
                </Box>
                {customMessageText.split("tip here")[1]}
              </>
            ) : (
              customMessageText || "Payment initiated"
            )}
          </Box>
        </Box>
      </MessageContent>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isOwn ? "flex-end" : "flex-start",
          gap: 1,
          mt: 1,
          width: "100%",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "rgba(0,0,0,0.6)",
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
          }}
        >
          {message.timestamp}
        </Typography>

        {isOwn && (
          <Typography
            variant="caption"
            sx={{
              color: "rgba(0,0,0,0.6)",
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
            }}
          >
            {message.isTemp ? (
              <AccessTime fontSize="inherit" />
            ) : (
              getReadReceiptStatus() || "✓"
            )}
          </Typography>
        )}
      </Box>
    </MessageBubbleContainer>
  );
};

export default PaymentRequestUpfrontMessage;
